import PropertySearchFilter from '../../property/filters';
import { newFeatureFilter, newPropertyFilter } from '../request-filters';
import { handleMunicipalityLayerClick } from '../layer-handlers';
import { filterLayerIfExists } from '../layers';

import {
    Coast,
    District,
    Entity,
    Island,
    Municipality,
    Neighbourhood,
    Province,
    Region,
} from '../../locations';
import SearchThisArea from '../components/search-this-area';
import PropertyResponse from '../models/property-response';
import { removeDuplicateItems } from '../../array';
import { getSourceFeatures, whenFeaturesAvailable } from '../features';
import DrawYourSearch from '../components/draw-your-search';
import Logger from '../../logging/logger';
import { captureException } from '../../logging/sentry';

const TotalRegions = 19;
const TotalProvinces = 52;

const onMapMoveEndLoadFeatures = async ( ctx, event ) => {
    ctx.setLoading( true );

    const bounds = ctx.map.getBounds();

    const boundSW = bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng;
    const boundNE = bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng;

    let featureTypeToLoad = null;
    let currentFeaturesOfTypeToLoad = [];

    let excludeLocationIDs = [];

    let currentLocation = ctx.currentLocation;

    if ( currentLocation && currentLocation.properties.type ) {
        switch ( currentLocation.properties.type ) {
        case Region:
            featureTypeToLoad = Province;
            break;
        case Province:
            featureTypeToLoad = Province;
            break;
        default:
            featureTypeToLoad = Province;
        }
    } else if ( ctx.activeComponents.has( SearchThisArea.id ) && ( !ctx.map.getLayer( `${Region}:outline` ) || ( ctx.map.getLayer( `${Region}:outline` ) && ctx.map.getLayoutProperty( `${Region}:outline`, 'visibility' ) === 'none' ) ) ) {
        featureTypeToLoad = Province;
    }

    const isIslandLocation = ( currentLocation && currentLocation.properties.type !== Island && currentLocation.properties.islandID > 0 );

    if ( featureTypeToLoad && currentLocation && currentLocation.properties.parents && currentLocation.properties.parents[ Province ] ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            ...currentLocation.properties.parents[ Province ].id,
        ];
    } else if ( featureTypeToLoad && currentLocation && currentLocation.properties.provinceID && !isIslandLocation ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            currentLocation.properties.provinceID,
        ];
    }

    const isIsland = ( currentLocation && ( currentLocation.properties.type === Island || currentLocation.properties.searchSynonymousForLocationType === Island ) );

    if ( featureTypeToLoad && isIsland ) {
        excludeLocationIDs = [ ...excludeLocationIDs, currentLocation.properties.id ];
    }

    if ( featureTypeToLoad && isIslandLocation ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            currentLocation.properties.islandID,
            currentLocation.properties.provinceID,
        ];
    }

    if ( featureTypeToLoad && ctx.map.getLayer( `${featureTypeToLoad}:fill` ) ) {
        const filter = ctx.map.getFilter( `${featureTypeToLoad}:fill` );

        if ( filter && filter.length === 2 ) {
            excludeLocationIDs = [
                ...excludeLocationIDs,
                ...filter[ 1 ][ 2 ],
            ];
        } else if ( filter && filter.length === 3 ) {
            excludeLocationIDs = [
                ...excludeLocationIDs,
                filter[ 2 ],
            ];
        }
    }

    const sourceTypeToLoad = ctx.map.getSource( featureTypeToLoad );

    if ( sourceTypeToLoad ) {
        const features = sourceTypeToLoad.serialize().data.features || [];
        excludeLocationIDs = [ ...excludeLocationIDs, features.map( ( f ) => parseInt( f.id ) ) ];
    }

    if ( featureTypeToLoad !== null ) {
        let src = ctx.map.getSource( featureTypeToLoad );

        if ( src ) {
            currentFeaturesOfTypeToLoad = src.serialize().data.features;
        }
    }

    const loadFeatures = () => {
        if ( featureTypeToLoad === null ) {
            return false;
        }

        if ( !currentLocation && !ctx.activeComponents.has( SearchThisArea.id ) ) {
            return false;
        }

        // we know there are only 19 regions
        if ( featureTypeToLoad && featureTypeToLoad === Region && currentFeaturesOfTypeToLoad.length === TotalRegions ) {
            return false;
        }

        // we know there are only 52 provinces
        if ( featureTypeToLoad === Province && currentFeaturesOfTypeToLoad.length === TotalProvinces ) {
            return false;
        }

        return true;
    };

    const fetchFeatures = loadFeatures();

    if ( !fetchFeatures ) {
        ctx.setLoading( false );
        return;
    }

    const options = {
        fetchFeatures: true,
        featureSearchURL: new URL( '/' + featureTypeToLoad, ctx.apiURL ),
        featureSearchParams: newFeatureFilter( {
            boundSW: boundSW,
            boundNE: boundNE,
            excludeLocationIDs: excludeLocationIDs,
        } ),
    };

    const meta = {
        appendFeaturesIfLayerExists: true,
        focusMapToSearch: false,
        filters: [],
        triggerStateChange: false,
        addArea: false,
    };

    Logger.info( '[map] Map Updating From Movement', {
        event,
        options,
        meta,
    } );

    await ctx.update( options, meta );

    ctx.setLoading( false );
};

const onMapMoveEnd = async ( ctx, event ) => {
    if ( ctx.isLoading || ctx.map.isZooming() ) {
        return;
    }

    const originalEvent = event.originalEvent;

    if ( originalEvent && originalEvent.type === 'resize' ) {
        return;
    }

    const isBoxAnimation = !!event.boxAnimation;

    if ( isBoxAnimation || ( !( 'originalEvent' in event ) && !event.geolocateSource ) ) {
        return;
    }

    const isGeoLocateRequest = 'geolocateSource' in event && event.geolocateSource === true;

    ctx.setLoading( true );

    const bounds = ctx.map.getBounds();

    const boundSW = bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng;
    const boundNE = bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng;

    const featureTypeToLoad = Province;
    let currentFeaturesOfTypeToLoad = [];

    let excludeLocationIDs = [];

    let currentLocation = ctx.currentLocation;

    const isIslandLocation = ( currentLocation && currentLocation.properties.type !== Island && currentLocation.properties.islandID > 0 );

    if ( featureTypeToLoad && currentLocation && currentLocation.properties.parents && currentLocation.properties.parents[ Province ] ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            ...currentLocation.properties.parents[ Province ].id,
        ];
    } else if ( featureTypeToLoad && currentLocation && currentLocation.properties.provinceID && !isIslandLocation ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            currentLocation.properties.provinceID,
        ];
    }

    const isIsland = ( currentLocation && ( currentLocation.properties.type === Island || currentLocation.properties.searchSynonymousForLocationType === Island ) );

    if ( featureTypeToLoad && isIsland ) {
        excludeLocationIDs = [ ...excludeLocationIDs, currentLocation.properties.id ];
    }

    if ( featureTypeToLoad && isIslandLocation ) {
        excludeLocationIDs = [
            ...excludeLocationIDs,
            currentLocation.properties.islandID,
            currentLocation.properties.provinceID,
        ];
    }

    const sourceTypeToLoad = ctx.map.getSource( featureTypeToLoad );

    if ( sourceTypeToLoad ) {
        const features = sourceTypeToLoad.serialize().data.features || [];
        excludeLocationIDs = [ ...excludeLocationIDs, features.map( ( f ) => parseInt( f.id ) ) ];
    }

    if ( featureTypeToLoad !== null ) {
        let src = ctx.map.getSource( featureTypeToLoad );

        if ( src ) {
            currentFeaturesOfTypeToLoad = src.serialize().data.features;
        }
    }

    let meta = {
        appendFeaturesIfLayerExists: true,
        focusMapToSearch: false,
        filters: [],
        triggerStateChange: false,
        addArea: false,
    };

    let options = {
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
    };

    const search = PropertySearchFilter.fromForm( ctx.baseForm );

    const loadFeatures = () => {
        if ( featureTypeToLoad === null ) {
            return false;
        }

        if ( !currentLocation && search.area.length === 0 ) {
            return false;
        }

        const regionLayer = ctx.map.getLayer( `${Region}:outline` );
        if ( regionLayer && regionLayer.visibility !== 'none' ) {
            return false;
        }

        // we know there are only 52 provinces
        if ( featureTypeToLoad === Province && currentFeaturesOfTypeToLoad.length === TotalProvinces ) {
            return false;
        }

        return true;
    };

    const fetchFeatures = loadFeatures();

    if ( fetchFeatures ) {
        options.fetchFeatures = true;
        options.featureSearchURL = new URL( '/' + featureTypeToLoad, ctx.apiURL );

        options.featureSearchParams = newFeatureFilter( {
            boundSW: boundSW,
            boundNE: boundNE,
            excludeLocationIDs: excludeLocationIDs,
        } );
    }

    options.propertySearchParams = newPropertyFilter( {
        boundSW: boundSW,
        boundNE: boundNE,
        onlyProperties: true,
    }, ctx.baseForm );

    if ( currentLocation && currentLocation.properties.municipalityID ) {
        let municipalityIDs;

        if ( typeof currentLocation.properties.municipalityID === 'string' ) {
            municipalityIDs = JSON.parse( currentLocation.properties.municipalityID || '[]' );
        } else {
            municipalityIDs = currentLocation.properties.municipalityID;
        }

        if ( municipalityIDs ) {
            for ( let i = 0; i < municipalityIDs.length; i++ ) {
                options.propertySearchParams.append( 'parentLocations', Municipality + ':' + municipalityIDs[ i ] );
            }
        }
    }

    if ( currentLocation && currentLocation.properties.type === Municipality ) {
        options.propertySearchParams.append( 'parentLocations', Municipality + ':' + currentLocation.properties.id );
    }

    const hasBoundsMax = ( currentLocation && currentLocation.properties.boundingBoxMaxLat && currentLocation.properties.boundingBoxMaxLng );
    const hasBoundsMin = ( currentLocation && currentLocation.properties.boundingBoxMinLat && currentLocation.properties.boundingBoxMinLng );

    if ( hasBoundsMax && hasBoundsMin ) {
        const locationBoundSW = currentLocation.properties.boundingBoxMaxLat + ',' + currentLocation.properties.boundingBoxMaxLng;
        const locationBoundNE = currentLocation.properties.boundingBoxMinLat + ',' + currentLocation.properties.boundingBoxMinLng;
        options.propertySearchParams.set( 'locationBoundSW', locationBoundSW );
        options.propertySearchParams.set( 'locationBoundNE', locationBoundNE );
    }

    options.propertySearchParams.delete( 'longitude' );
    options.propertySearchParams.delete( 'latitude' );

    if ( search.anyLocationFilters() ) {
        options.propertySearchParams.set( 'existingLocations', removeDuplicateItems( getSourceFeatures( ctx.map, Municipality ).map( ( hit ) => hit.id ) ) );
    }

    Logger.info( '[map] Map Updating From Movement', {
        event,
        options,
        meta,
        isGeoLocateRequest,
        isBoxAnimation,
    } );

    const results = await ctx.update( options, meta );

    if ( isGeoLocateRequest && ctx.userPosition.lat && ctx.userPosition.lon ) {
        let layersToQuery = [
            Region,
            Province,
            Coast,
            Island,
            Municipality,
            District,
            Neighbourhood,
            Entity,
        ];

        layersToQuery = layersToQuery.map( ( layer ) => `${layer}:fill` );

        layersToQuery = layersToQuery.filter( ( layer ) => !!ctx.map.getLayer( layer ) );

        // convert from lat/lon to x/y pixels
        const position = ctx.map.project( {
            lat: ctx.userPosition.lat,
            lon: ctx.userPosition.lon,
        } );

        const featuresAtUserLocation = ctx.map.queryRenderedFeatures( [ position.x, position.y ], {
            validate: ctx.debug,
            layers: layersToQuery,
        } );

        const directlyAcceptedLayers = [
            Municipality,
            District,
            Neighbourhood,
            Entity,
        ];

        if ( featuresAtUserLocation.length > 0 && directlyAcceptedLayers.includes( featuresAtUserLocation[ 0 ].source ) ) {
            const featureToUse = featuresAtUserLocation[ 0 ];

            await handleMunicipalityLayerClick( ctx, new CustomEvent( 'twc:mapsearch:click-Municipality', {
                bubbles: true,
                detail: {
                    originalEvent: event,
                    feature: featureToUse,
                },
            } ) );

            ctx.setLoading( false );
            return;
        }

        const pointURL = new URL( '/shapeContainingPoint', ctx.apiURL );
        pointURL.searchParams.set( 'shapeType', Municipality );
        pointURL.searchParams.set( 'latitude', ctx.userPosition.lat );
        pointURL.searchParams.set( 'longitude', ctx.userPosition.lon );

        const response = await http( pointURL );

        if ( !response.ok ) {
            ctx.setLoading( false );
            captureException( new Error( 'failed to get location for user locations' ) );
            return;
        }

        const feature = await response.json();

        if ( feature.properties.provinceID > 0 ) {
            const provinceFilter = [
                '!=', [ 'get', 'id' ], feature.properties.provinceID,
            ];

            filterLayerIfExists( ctx.map, `${Province}:fill`, provinceFilter, ctx.debug );
            filterLayerIfExists( ctx.map, `${Province}:outline`, provinceFilter, ctx.debug );
        }

        if ( feature.properties.regionID > 0 ) {
            const regionFilter = [
                '!=', [ 'get', 'id' ], feature.properties.regionID,
            ];

            filterLayerIfExists( ctx.map, `${Region}:fill`, regionFilter, ctx.debug );
            filterLayerIfExists( ctx.map, `${Region}:outline`, regionFilter, ctx.debug );
        }

        await handleMunicipalityLayerClick( ctx, new CustomEvent( 'twc:mapsearch:click-Municipality', {
            bubbles: true,
            detail: {
                originalEvent: event,
                feature: feature,
            },
        } ) );

        if ( ctx.map.getSource( Municipality ) ) {
            const state = {
                active: true,
            };

            const filter = {
                source: Municipality,
                id: feature.id,
            };

            ctx.map.setFeatureState( filter, state );
        }
    }

    if ( search.anyLocationFilters() && search.locationValue !== DrawYourSearch.id && search.locationValue !== SearchThisArea.id ) {
        const response = results.find( ( r ) => r instanceof PropertyResponse ).response;

        const matchedLocations = response.locationFilterMatches;

        if ( !matchedLocations || matchedLocations.length === 0 ) {
            ctx.setLoading( false );
            return;
        }

        const existingMunicipalities = removeDuplicateItems( getSourceFeatures( ctx.map, Municipality ).map( ( hit ) => hit.id ) );

        await ctx.update( {
            fetchFeatures: true,
            featureSearchParams: newFeatureFilter( {
                locationIds: matchedLocations.filter( ( id ) => !existingMunicipalities.includes( id ) ),
                excludeLocationIDs: existingMunicipalities,
            } ),
            featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
            fetchProperties: false,
            expectProperties: false,
        }, {
            focusMapToSearch: false,
            appendFeaturesIfLayerExists: true,
            triggerStateChange: false,
        } );

        whenFeaturesAvailable( ctx.map, {
            source: Municipality,
        }, ctx.abortController.signal ).then( ( features ) => {
            for ( let i = 0; i < features.length; i++ ) {
                ctx.map.setFeatureState(
                    {
                        source: Municipality,
                        id: features[ i ].id,
                    },
                    {
                        active: true,
                    } );
            }
        } )
            .catch( ( err ) => {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            } );
    }

    ctx.setLoading( false );
};

export {
    onMapMoveEnd,
    onMapMoveEndLoadFeatures,
};
